import React, { useCallback } from 'react';

import { FloorChip, FloorChipsRow } from './styles';
import { Floorplan } from '../types';

interface FloorChipsProps {
  floorplans: Floorplan[];
  activeFloorplanUuid?: string;
  onSelect: (floorplanUuid: string) => void;
}

interface FloorChipItemProps {
  floorplan: Floorplan;
  isActive: boolean;
  onSelect: (floorplanUuid: string) => void;
}

const FloorChipItem = ({ floorplan, isActive, onSelect }: FloorChipItemProps): JSX.Element => {
  const handleClick = useCallback(() => onSelect(floorplan.uuid), [onSelect, floorplan.uuid]);

  return (
    <FloorChip
      type="button"
      data-testid={`floor-chip-${floorplan.uuid}`}
      isActive={isActive}
      aria-pressed={isActive}
      onClick={handleClick}
    >
      {floorplan.name}
    </FloorChip>
  );
};

/**
 * Floor switcher: one chip per floorplan, in the order the admin arranged them.
 *
 * A single floorplan needs no switcher, so nothing renders — a lone chip would only take space and
 * suggest there is somewhere else to go.
 */
export const FloorChips = ({ floorplans, activeFloorplanUuid, onSelect }: FloorChipsProps): JSX.Element | null => {
  if (floorplans.length < 2) {
    return null;
  }

  return (
    <FloorChipsRow data-testid="floor-chips">
      {floorplans.map(floorplan => (
        <FloorChipItem
          key={floorplan.uuid}
          floorplan={floorplan}
          isActive={floorplan.uuid === activeFloorplanUuid}
          onSelect={onSelect}
        />
      ))}
    </FloorChipsRow>
  );
};
